import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Camera,
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Info,
  Sparkles,
  ClipboardList,
  Download,
  Play,
  HelpCircle,
  Lock,
  RefreshCw,
  X,
  FileText,
  User,
  Check,
  Zap,
} from 'lucide-react';
import ConorHarrisVideoPlayer from '@/components/posture-analysis/self-assessment/ConorHarrisVideoPlayer';
import SurveyBookingWidget, {
  DEFAULT_SURVEY_BASE_URL,
  type EnsureProspectForBookingInput,
} from '@/components/posture-analysis/self-assessment/SurveyBookingWidget';
import { AnnotatedImage } from '@/components/posture-analysis/AnnotatedImage';
import { SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID } from '@/components/posture-analysis/lib/posture-mode-strategy';
import { DEFAULT_ADULT_HEIGHT_CM } from '@/components/posture-analysis/services/posture-settings-firebase';
import { exportSelfAssessmentReportToPdf } from '@/components/posture-analysis/self-assessment/export-self-assessment-pdf';
import { buildSimpleSaLiveSummary } from '@/components/posture-analysis/self-assessment/simple-sa-live-summary';
import { evaluateFullPostureAnalysis } from '@/lib/posture-analysis';
import type { PostureAnalysis } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import logger from '@/lib/logging';
import { useSelfAssessmentPageSetup } from '@/components/posture-analysis/self-assessment/hooks/useSelfAssessmentPageSetup';
import { persistProspectGhlCalendarEvent } from '@/components/posture-analysis/self-assessment/client-prospects-api';
import { useSelfAssessmentProspect } from '@/components/posture-analysis/self-assessment/hooks/useSelfAssessmentProspect';
import { afterStrictModeMount } from '@/lib/after-strict-mode-mount';
import {
  trackSelfAssessmentStep,
  trackPhotoUploadStart,
  trackPhotoUploadSuccess,
  trackSelfAssessmentCompleted,
  trackSelfAssessmentAnalysisSuccess,
  trackReportPdfDownloaded,
} from '@/lib/analytics/analytics-service';
import {
  HYROS_FUNNEL_EVENTS,
  installHyrosFunnelScript,
  reportHyrosFunnelEmail,
  trackHyrosFunnelEvent,
} from '@/lib/analytics/hyros-funnel';

export type FunnelStep = 1 | 2 | 3 | 4;

export const SIMPLE_SA_SURVEY_LEAD_IN =
  'If you want to go over your results with one of our specialists:';

export const SIMPLE_SA_SURVEY_HEADLINE = 'Fill out this form and book a call';

const OPTIONAL_PHOTO_CONSENT =
  'Optional: I also consent to hArry storing my posture photos with this report.';

interface PhotoState {
  file: File | null;
  previewUrl: string | null;
}

export const SelfAssessmentSimplePage: React.FC = () => {
  const [step, setStep] = useState<FunnelStep>(1);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatusText, setScanStatusText] = useState('Detecting key anatomical landmarks...');

  // Step 2: Upfront Lead Intake
  const [leadName, setLeadName] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.sessionStorage.getItem('sa_lead_name') || '';
    }
    return '';
  });
  const [leadEmail, setLeadEmail] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.sessionStorage.getItem('sa_lead_email') || '';
    }
    return '';
  });
  const [contactConsent, setContactConsent] = useState(false);
  const [pictureConsent, setPictureConsent] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const rememberLeadContact = useCallback((contact?: { name?: string; email?: string }) => {
    const nextName = contact?.name?.trim() || '';
    const nextEmail = contact?.email?.trim().toLowerCase() || '';
    if (nextName) setLeadName(nextName);
    if (nextEmail) {
      setLeadEmail(nextEmail);
      reportHyrosFunnelEmail(nextEmail);
    }
    try {
      if (nextName) window.sessionStorage.setItem('sa_lead_name', nextName);
      if (nextEmail) window.sessionStorage.setItem('sa_lead_email', nextEmail);
    } catch {
      // ignore storage errors in incognito
    }
  }, []);

  // Step 3: Photo Collection
  const [frontPhoto, setFrontPhoto] = useState<PhotoState>({ file: null, previewUrl: null });
  const [sidePhoto, setSidePhoto] = useState<PhotoState>({ file: null, previewUrl: null });

  // On-demand info drawers/modals state ("Beamed-In" info architecture)
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPositioningGuide, setShowPositioningGuide] = useState(false);
  const [showFrontConorExample, setShowFrontConorExample] = useState(false);
  const [showSideConorExample, setShowSideConorExample] = useState(false);
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Step 5: Report Delivery & Download
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [analysisError, setAnalysisError] = useState(false);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const sideInputRef = useRef<HTMLInputElement>(null);

  const [capturedLandmarks, setCapturedLandmarks] = useState<Record<string, { landmarks: any[]; cmPerPx: number }>>({});
  // Holds the real computed PostureAnalysis (angles + classification) once landmarks are detected
  const [liveAnalysis, setLiveAnalysis] = useState<PostureAnalysis | null>(null);
  const hasTrackedAnalysisSuccessRef = useRef(false);
  const contactSnapshotStep = useRef<number | null>(null);
  const hadContactOnEnter = useRef(false);

  const { createProspect, linkAnalysis } = useSelfAssessmentProspect();
  const bookingProspectIdRef = useRef<string | null>(
    typeof window !== 'undefined' ? window.sessionStorage.getItem('sa_prospect_id') : null,
  );

  const ensureProspectForBooking = useCallback(
    async (input: EnsureProspectForBookingInput): Promise<string | null> => {
      // Survey contact submit implies consent to persist for the walkthrough call.
      const nameToUse = input.name.trim() || leadName.trim() || input.email.trim();
      const emailToUse = input.email.trim() || leadEmail.trim();
      const prospectId =
        bookingProspectIdRef.current ||
        (await createProspect(
          {
            name: nameToUse,
            email: emailToUse,
            ...(input.ghlSurvey ? { ghlSurvey: input.ghlSurvey } : {}),
          },
          true,
          pictureConsent,
        ));
      if (prospectId) {
        bookingProspectIdRef.current = prospectId;
        if (liveAnalysis) {
          void linkAnalysis(prospectId, liveAnalysis, true, pictureConsent).catch((err) => {
            logger.error('[SelfAssessmentSimplePage] link analysis for booking failed', {
              error: err instanceof Error ? err.message : String(err),
            });
          });
        }
        // Calendar handoff is the only caller — this means they opened the GHL calendar.
        trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.calendarOpened);
        void persistProspectGhlCalendarEvent(prospectId, 'opened');
      }
      return prospectId;
    },
    [createProspect, linkAnalysis, liveAnalysis, pictureConsent, leadName, leadEmail],
  );

  const handleLandmarksDetected = useCallback((view: 'Anterior' | 'Right' | 'Left', landmarks: any[], cmPerPx: number) => {
    setCapturedLandmarks((prev) => ({
      ...prev,
      [view]: { landmarks, cmPerPx },
    }));
    trackPhotoUploadSuccess(view);
    trackHyrosFunnelEvent(
      view === 'Anterior' ? HYROS_FUNNEL_EVENTS.photoFront : HYROS_FUNNEL_EVENTS.photoSide,
    );
    const frontReady = view === 'Anterior' || Boolean(capturedLandmarks.Anterior?.landmarks?.length);
    const sideReady = view === 'Right' || Boolean(capturedLandmarks.Right?.landmarks?.length);
    if (frontReady && sideReady) {
      trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.photosReady);
    }
  }, [capturedLandmarks.Anterior?.landmarks?.length, capturedLandmarks.Right?.landmarks?.length]);

  // Run real posture analysis (angles + classification) whenever both landmark sets are available.
  // Uses the single authoritative central posture analysis pipeline with auto floor leveling.
  useEffect(() => {
    const anteriorData = capturedLandmarks.Anterior;
    const rightData = capturedLandmarks.Right;
    if (!anteriorData?.landmarks?.length && !rightData?.landmarks?.length) return;
    if (!frontPhoto.previewUrl && !sidePhoto.previewUrl) return;

    try {
      const rawCaptured: Record<string, any> = {};

      if (anteriorData?.landmarks?.length && frontPhoto.previewUrl) {
        rawCaptured.Anterior = {
          imageData: frontPhoto.previewUrl,
          landmarks: anteriorData.landmarks,
          cmPerPx: anteriorData.cmPerPx,
          status: 'complete',
        };
      }

      if (rightData?.landmarks?.length && sidePhoto.previewUrl) {
        rawCaptured.Right = {
          imageData: sidePhoto.previewUrl,
          landmarks: rightData.landmarks,
          cmPerPx: rightData.cmPerPx,
          status: 'complete',
        };
      }

      if (Object.keys(rawCaptured).length === 0) return;

      const evalResult = evaluateFullPostureAnalysis({
        captured: rawCaptured,
        clientHeightCM: DEFAULT_ADULT_HEIGHT_CM,
        unit: 'cm',
        clientSex: 'Unknown',
        strategyId: SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID,
      });

      const { analysis, captured, classification } = evalResult;

      if (!classification?.pattern) {
        setAnalysisError(true);
        return;
      }

      setAnalysisError(false);
      setLiveAnalysis({
        id: `self-assessment-simple-${Date.now()}`,
        clientHeight: DEFAULT_ADULT_HEIGHT_CM,
        unit: 'cm',
        postureOrientationStrategyId: SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID,
        captured,
        analysis,
        classification,
        createdAt: new Date(),
      } as PostureAnalysis);
    } catch (err) {
      logger.warn('[SelfAssessmentSimplePage] Live analysis error (non-fatal):', err);
      setAnalysisError(true);
    }
  }, [capturedLandmarks, frontPhoto.previewUrl, sidePhoto.previewUrl]);

  // Display shell for MediaPipe AnnotatedImage until live classification is ready.
  // Never invents a pattern — Step 3 waits for `liveAnalysis.classification`.
  const simplePostureAnalysis: PostureAnalysis = React.useMemo(() => {
    if (liveAnalysis) return liveAnalysis;
    return {
      id: `self-assessment-simple-pending`,
      clientHeight: DEFAULT_ADULT_HEIGHT_CM,
      unit: 'cm',
      postureOrientationStrategyId: SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID,
      captured: {
        Anterior: {
          imageData: frontPhoto.previewUrl || '',
          landmarks: capturedLandmarks.Anterior?.landmarks || [],
          cmPerPx: capturedLandmarks.Anterior?.cmPerPx || 0.2,
          floorCorrectionDeg: 0,
          status: 'complete',
        },
        Right: {
          imageData: sidePhoto.previewUrl || '',
          landmarks: capturedLandmarks.Right?.landmarks || [],
          cmPerPx: capturedLandmarks.Right?.cmPerPx || 0.2,
          floorCorrectionDeg: 0,
          status: 'complete',
        },
      },
      createdAt: new Date(),
    } as PostureAnalysis;
  }, [liveAnalysis, frontPhoto.previewUrl, sidePhoto.previewUrl, capturedLandmarks]);

  const liveSummary = React.useMemo(() => buildSimpleSaLiveSummary(liveAnalysis), [liveAnalysis]);

  // Count a user who uploaded both required photos and received a live classification.
  // Fire on the results step or the final send step. Landmark object identity must not
  // cancel afterStrictModeMount; leaving step 4 used to skip ingest entirely.
  useEffect(() => {
    if (hasTrackedAnalysisSuccessRef.current) return;
    if (step < 3) return;
    if (!liveAnalysis?.id || !liveSummary) return;
    const hasFront = Boolean(frontPhoto.previewUrl && capturedLandmarks.Anterior?.landmarks?.length);
    const hasSide = Boolean(sidePhoto.previewUrl && capturedLandmarks.Right?.landmarks?.length);
    if (!hasFront || !hasSide) return;
    return afterStrictModeMount(() => {
      if (hasTrackedAnalysisSuccessRef.current) return;
      hasTrackedAnalysisSuccessRef.current = true;
      trackSelfAssessmentAnalysisSuccess({
        viewsCaptured: 2,
        pattern: liveAnalysis.classification?.pattern,
      });
      trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.resultsReady);
      const capturedCount = Object.values(liveAnalysis.captured ?? {}).filter(Boolean).length;
      trackSelfAssessmentCompleted(capturedCount);
    });
  }, [
    step,
    liveAnalysis?.id,
    liveAnalysis?.classification?.pattern,
    liveSummary,
    frontPhoto.previewUrl,
    sidePhoto.previewUrl,
    capturedLandmarks.Anterior?.landmarks?.length,
    capturedLandmarks.Right?.landmarks?.length,
  ]);

  // Auto-link liveAnalysis to upfront prospect once calculated
  useEffect(() => {
    if (!liveAnalysis || !bookingProspectIdRef.current) return;
    void linkAnalysis(
      bookingProspectIdRef.current,
      liveAnalysis,
      contactConsent,
      pictureConsent,
    ).catch((err) => {
      logger.warn('[SelfAssessmentSimplePage] Auto link analysis error:', err);
    });
  }, [liveAnalysis, contactConsent, pictureConsent, linkAnalysis]);

  // Shared page-setup: force light theme + enable document scroll.
  useSelfAssessmentPageSetup();

  // Track page load on mount.
  useEffect(
    () =>
      afterStrictModeMount(() => {
        installHyrosFunnelScript();
        trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.funnelOpened);
        trackSelfAssessmentStep('start');
      }),
    [],
  );

  useEffect(() => {
    if (!showBookingModal) return;
    trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.applicationOpened);
  }, [showBookingModal]);

  // Instant scroll. A smooth scroll from a long results page leaves the shorter
  // previous step below the fold, which looks like an empty screen.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.getElementById('root')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  useEffect(() => {
    if (step < 3) setIsScanning(false);
    if (step === 4) trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.pdfFormOpened);
  }, [step]);

  useEffect(() => {
    window.history.replaceState({ saStep: 1 }, '');
  }, []);

  const applyingHistoryRef = React.useRef(false);
  useEffect(() => {
    if (applyingHistoryRef.current) {
      applyingHistoryRef.current = false;
      return;
    }
    const current = (window.history.state as { saStep?: number } | null)?.saStep;
    if (current === step) return;
    window.history.pushState({ saStep: step }, '');
  }, [step]);

  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      setShowBookingModal(false);
      const next = (event.state as { saStep?: number } | null)?.saStep;
      const stepNumber = next === 5 ? 4 : next === 1 || next === 2 || next === 3 || next === 4 ? next : 1;
      applyingHistoryRef.current = true;
      setStep(stepNumber);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const openPdfFromSurveyLink = () => {
      if (window.location.hash !== '#harry-download-report') return;
      setShowBookingModal(false);
      setStep(4);
      const nextUrl = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState({ saStep: 4 }, '', nextUrl);
    };
    window.addEventListener('hashchange', openPdfFromSurveyLink);
    openPdfFromSurveyLink();
    return () => window.removeEventListener('hashchange', openPdfFromSurveyLink);
  }, []);

  // Handle Photo Upload
  const handlePhotoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'front' | 'side'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const viewName = type === 'front' ? 'Anterior' : 'Right';
    trackPhotoUploadStart(viewName, { fileType: file.type, fileSize: file.size });

    const previewUrl = URL.createObjectURL(file);
    if (type === 'front') {
      setFrontPhoto({ file, previewUrl });
    } else {
      setSidePhoto({ file, previewUrl });
    }
    // Landmarks detected callback fires after AnnotatedImage processes the image;
    // trackPhotoUploadSuccess is called there via handleLandmarksDetected.
  };

  const removePhoto = (type: 'front' | 'side') => {
    const viewName = type === 'front' ? 'Anterior' : 'Right';
    if (type === 'front') {
      setFrontPhoto({ file: null, previewUrl: null });
      if (frontInputRef.current) frontInputRef.current.value = '';
    } else {
      setSidePhoto({ file: null, previewUrl: null });
      if (sideInputRef.current) sideInputRef.current.value = '';
    }
    setCapturedLandmarks((prev) => {
      const next = { ...prev };
      delete next[viewName];
      return next;
    });
    setLiveAnalysis(null);
    setAnalysisError(false);
    hasTrackedAnalysisSuccessRef.current = false;
  };

  const startAnalysisSequence = () => {
    setAnalysisError(false);
    setStep(3);
    trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.analysisStarted);
    if (liveSummary) {
      setIsScanning(false);
      setScanProgress(100);
      return;
    }
    setIsScanning(true);
    setScanProgress(0);
    setScanStatusText('Detecting anatomical landmarks with AI...');
  };

  useEffect(() => {
    if (step !== 3 || !isScanning) return;
    const statuses = [
      'Detecting anatomical landmarks with AI...',
      'Measuring sagittal alignment...',
      'Evaluating cervical and tibial angles...',
      'Classifying biomechanical posture pattern...',
      'Finalizing live posture report...',
    ];
    const interval = window.setInterval(() => {
      setScanProgress((prev) => {
        const next = prev >= 90 ? 90 : prev + 10;
        setScanStatusText(statuses[Math.min(Math.floor(next / 20), statuses.length - 1)]);
        return next;
      });
    }, 400);
    const timeout = window.setTimeout(() => {
      if (!buildSimpleSaLiveSummary(liveAnalysis)) {
        setAnalysisError(true);
        setIsScanning(false);
        trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.analysisFailed);
      }
    }, 90_000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [step, isScanning, liveAnalysis]);

  useEffect(() => {
    if (!liveSummary) return;
    setScanProgress(100);
    setIsScanning(false);
    setAnalysisError(false);
  }, [liveSummary]);

  const handleDownloadPdf = async () => {
    if (isDownloading || !liveAnalysis?.classification) return;
    let contactName = leadName.trim();
    let contactEmail = leadEmail.trim();
    try {
      contactName = contactName || window.sessionStorage.getItem('sa_lead_name')?.trim() || '';
      contactEmail = contactEmail || window.sessionStorage.getItem('sa_lead_email')?.trim() || '';
    } catch {
      // ignore storage errors in incognito
    }
    setIsDownloading(true);
    try {
      if (bookingProspectIdRef.current && liveAnalysis) {
        void linkAnalysis(
          bookingProspectIdRef.current,
          liveAnalysis,
          contactConsent,
          pictureConsent,
        ).catch(() => {});
      }
      await exportSelfAssessmentReportToPdf(liveAnalysis, {
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        hideExercises: true,
      });
      setDownloadSuccess(true);
      trackReportPdfDownloaded({
        has_contact_name: leadName.trim() ? 'true' : 'false',
        source: 'simple',
      });
      trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.reportDownloaded);
    } catch (err) {
      logger.error('[SelfAssessmentSimple] PDF Export error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Last step: name and email, then the PDF. Analysis has already run.
  const handleSendResults = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadEmail.trim() || !leadName.trim() || !contactConsent) return;
    reportHyrosFunnelEmail(leadEmail);
    setIsSubmittingLead(true);
    setLeadError(null);
    try {
      let prospectId = bookingProspectIdRef.current;
      if (!prospectId) {
        prospectId = await createProspect(
          { name: leadName.trim(), email: leadEmail.trim() },
          contactConsent,
          pictureConsent,
        );
        if (prospectId) {
          bookingProspectIdRef.current = prospectId;
          try {
            window.sessionStorage.setItem('sa_prospect_id', prospectId);
            window.sessionStorage.setItem('sa_lead_name', leadName.trim());
            window.sessionStorage.setItem('sa_lead_email', leadEmail.trim());
          } catch {
            // ignore storage errors in incognito
          }
        }
      }
      if (prospectId && liveAnalysis) {
        await linkAnalysis(prospectId, liveAnalysis, contactConsent, pictureConsent);
      }
      trackSelfAssessmentStep('lead_intake');
      await handleDownloadPdf();
    } catch (err) {
      logger.error('[SelfAssessmentSimplePage] Send results error:', err);
      setLeadError('Something went wrong. Please try again.');
    } finally {
      setIsSubmittingLead(false);
    }
  };

  if (contactSnapshotStep.current !== step) {
    contactSnapshotStep.current = step;
    hadContactOnEnter.current = step === 4 && Boolean(leadEmail.trim() && contactConsent);
  }
  const showSavedContact = hadContactOnEnter.current && Boolean(leadEmail.trim() && contactConsent);

  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#161e2a] font-sans selection:bg-[#8e44ad] selection:text-white">
      {/* Background Subtle Soft Light Gradients */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-500/8 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 -right-40 w-96 h-96 bg-indigo-500/8 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-emerald-500/6 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-6 sm:py-10 flex flex-col min-h-screen">
        {/* Header Bar & Step Progress */}
        <header className="mb-6 flex items-center justify-between border-b border-[#ecf0f1] pb-4">
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg tracking-tight text-[#161e2a]">
              hArry Posture AI
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPrivacyModal(true)}
              className="inline-flex items-center gap-1 text-sm text-[#595959] hover:text-[#8e44ad] transition-colors py-1 px-2.5 rounded-md hover:bg-purple-50"
            >
              <Lock className="w-3.5 h-3.5 text-emerald-600" />
              <span className="hidden sm:inline font-medium">100% Private</span>
            </button>
            <div className="text-xs font-semibold text-[#8e44ad] bg-purple-50 border border-purple-200 px-3 py-1 rounded-full">
              Step {step} of 4
            </div>
          </div>
        </header>

        {/* Top Minimal Step Progress Bar */}
        {(frontPhoto.previewUrl || sidePhoto.previewUrl) && (
          <div
            className="fixed left-[-2000px] top-0 w-[320px] h-[400px] overflow-hidden pointer-events-none"
            aria-hidden
            data-testid="simple-sa-landmark-detectors"
          >
            {frontPhoto.previewUrl && (
              <AnnotatedImage
                view="Anterior"
                analysis={simplePostureAnalysis}
                onLandmarksDetected={handleLandmarksDetected}
              />
            )}
            {sidePhoto.previewUrl && (
              <AnnotatedImage
                view="Right"
                analysis={simplePostureAnalysis}
                onLandmarksDetected={handleLandmarksDetected}
              />
            )}
          </div>
        )}

        <div className="w-full bg-slate-200/80 h-2 rounded-full overflow-hidden mb-8 border border-[#ecf0f1]">
          <div
            className="bg-gradient-to-r from-[oklch(52%_0.18_305)] via-purple-600 to-indigo-600 h-full transition-all duration-500 ease-out"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        {/* ── STEP 1: THE HOOK (Landing) ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 flex-1 flex flex-col justify-center py-4">
            <div className="text-center space-y-4 mb-8">
              <Badge variant="outline" className="bg-purple-50 text-[#8e44ad] border-purple-200 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#8e44ad] inline" />
                AI Posture Analysis
              </Badge>

              <h1 className="text-3xl sm:text-5xl font-extrabold text-[#161e2a] tracking-tight leading-tight">
                Find Your Posture Pattern in <span className="bg-gradient-to-r from-[oklch(52%_0.18_305)] via-purple-600 to-indigo-700 bg-clip-text text-transparent">60 Seconds</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-700 max-w-lg mx-auto leading-relaxed">
                hArry uses AI-guided biomechanics to spot Anterior Pelvic Tilt, Swayback, or side-to-side imbalance from 2 photos — then recommends individualized exercises that address your condition.
              </p>

              <div className="pt-2 flex items-center justify-center gap-4 text-sm text-[#595959] font-medium">
                <span className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                  Instant AI Results
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#8e44ad]" />
                  No App Needed
                </span>
              </div>
            </div>

            {/* SQUINT TEST WINNER: Single Massive Contrasting CTA — above hero */}
            <div className="space-y-4 max-w-md mx-auto w-full mb-6">
              <button
                onClick={() => {
                  setStep(2);
                  trackSelfAssessmentStep('anterior');
                  trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.assessmentStarted);
                }}
                className="w-full py-5 px-8 rounded-2xl bg-gradient-to-r from-[oklch(52%_0.18_305)] via-purple-600 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-extrabold text-xl shadow-xl shadow-purple-900/20 hover:shadow-purple-900/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-3 border border-purple-400/20 cursor-pointer"
              >
                <span>Start Free Assessment</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-center text-sm text-[#595959]">
                100% Free • No credit card required • Instant report
              </p>
            </div>

            {/* HERO PREVIEW GRAPHIC SHOWCASE */}
            <div className="mb-8 max-w-sm sm:max-w-md mx-auto w-full rounded-2xl overflow-hidden shadow-lg border border-[#ecf0f1] bg-white p-2 animate-in fade-in zoom-in-95 duration-500">
              <img
                src="/self-assessment/conor-hero-front-page-pic.png"
                alt="Conor Harris Posture AI Analysis Example"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>

            {/* ON-DEMAND INFORMATION ("BEAMED-IN" SYSTEM) */}
            <div className="mt-auto border-t border-[#ecf0f1] pt-6 text-center space-y-3">
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <button
                  onClick={() => setShowHowItWorksModal(true)}
                  className="text-[#595959] hover:text-[#8e44ad] font-medium inline-flex items-center gap-1 underline underline-offset-4 cursor-pointer"
                >
                  <Info className="w-3.5 h-3.5" />
                  How does the AI analysis work?
                </button>
                <button
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-[#595959] hover:text-[#8e44ad] font-medium inline-flex items-center gap-1 underline underline-offset-4 cursor-pointer"
                >
                  <Lock className="w-3.5 h-3.5" />
                  Privacy &amp; Data Protection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: DOWNLOAD THE REPORT (last) ───────────────────────────── */}
        {step === 4 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500 flex-1 flex flex-col justify-between py-2">
            <div>
              <div className="text-center mb-6">
                <Badge className="bg-purple-50 text-[#8e44ad] border-purple-200 mb-2">
                  Instant Personalization • 100% Free
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#161e2a] mb-2">
                  Download Your Results
                </h2>
                <p className="text-base sm:text-lg text-slate-700 max-w-md mx-auto leading-relaxed">
                  Enter your name and email to save your session and download your posture report.
                </p>
              </div>

              {downloadSuccess ? (
                <div className="bg-white border border-emerald-300 rounded-2xl p-6 text-center space-y-4 shadow-xl my-6 max-w-md mx-auto">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-bold text-[#161e2a]">Your PDF Report Is Ready!</h3>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                    Your posture report has been generated and downloaded to your device. You can download another copy anytime below.
                  </p>
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={isDownloading}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl gap-2 shadow-lg shadow-emerald-600/20 cursor-pointer disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" /> Generating Your PDF Report...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" /> Download Report PDF Again
                      </>
                    )}
                  </Button>
                </div>
              ) : showSavedContact ? (
                <Card className="bg-white border-[#ecf0f1] shadow-xl mb-6 max-w-md mx-auto">
                  <CardContent className="p-6 space-y-4">
                    <div className="bg-purple-50/70 border border-purple-100 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-[#8e44ad]">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-bold text-base text-[#161e2a]">
                            {leadName || 'Your Custom Report'}
                          </div>
                          <div className="text-sm sm:text-base text-slate-700 font-medium">
                            {leadEmail}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setContactConsent(false)}
                        className="text-sm text-[#8e44ad] hover:underline font-medium cursor-pointer"
                      >
                        Change
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={isDownloading}
                      className="w-full py-4 px-6 rounded-xl bg-[oklch(52%_0.18_305)] hover:bg-purple-800 text-white font-bold text-base shadow-lg shadow-purple-900/20 flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
                    >
                      {isDownloading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Generating PDF Report...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Download PDF Report
                        </>
                      )}
                    </button>
                  </CardContent>
                </Card>
              ) : (
              <Card className="bg-white border-[#ecf0f1] shadow-xl mb-6 max-w-md mx-auto">
                <CardContent className="p-6 space-y-4">
                  <form onSubmit={handleSendResults} className="space-y-4">
                    <div>
                      <label htmlFor="sa-simple-lead-name" className="block text-sm font-semibold text-[#161e2a] mb-1">Your Name</label>
                      <div className="relative">
                        <User className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                        <input
                          id="sa-simple-lead-name"
                          type="text"
                          required
                          placeholder="e.g. John Doe"
                          value={leadName}
                          onChange={(e) => setLeadName(e.target.value)}
                          className="w-full bg-[#f9f9f9] border border-slate-300 rounded-xl py-3 pl-10 pr-4 text-sm text-[#161e2a] placeholder-slate-400 focus:outline-none focus:border-[#8e44ad] focus:bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="sa-simple-lead-email" className="block text-sm font-semibold text-[#161e2a] mb-1">Email Address</label>
                      <div className="relative">
                        <FileText className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                        <input
                          id="sa-simple-lead-email"
                          type="email"
                          required
                          placeholder="e.g. john@example.com"
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          className="w-full bg-[#f9f9f9] border border-slate-300 rounded-xl py-3 pl-10 pr-4 text-sm text-[#161e2a] placeholder-slate-400 focus:outline-none focus:border-[#8e44ad] focus:bg-white"
                        />
                      </div>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        id="sa-simple-lead-consent"
                        checked={contactConsent}
                        onChange={(e) => setContactConsent(e.target.checked)}
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-[#8e44ad] cursor-pointer"
                      />
                      <span className="text-sm sm:text-base text-slate-700 leading-relaxed">
                        I consent to hArry storing my name and email securely for this report and follow-up. I can request deletion at any time.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        id="sa-simple-picture-consent"
                        checked={pictureConsent}
                        onChange={(e) => setPictureConsent(e.target.checked)}
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-[#8e44ad] cursor-pointer"
                      />
                      <span className="text-sm sm:text-base text-slate-700 leading-relaxed">
                        {OPTIONAL_PHOTO_CONSENT}
                      </span>
                    </label>

                    {leadError && (
                      <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
                        {leadError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmittingLead || isDownloading || !contactConsent || !leadEmail.trim() || !leadName.trim()}
                      className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-[oklch(52%_0.18_305)] via-purple-600 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-extrabold text-base shadow-lg shadow-purple-900/20 flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
                    >
                      {isSubmittingLead || isDownloading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Preparing your report...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5" />
                          <span>Download PDF Report</span>
                        </>
                      )}
                    </button>
                  </form>

                  <div className="pt-1 text-center text-xs text-[#595959] flex items-center justify-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-emerald-600" />
                    We respect your privacy. Zero spam guaranteed.
                  </div>
                </CardContent>
              </Card>
              )}

              <div className="text-center">
                <button
                  onClick={() => setShowPdfPreviewModal(true)}
                  className="text-sm text-[#8e44ad] hover:text-purple-800 underline underline-offset-4 inline-flex items-center gap-1 cursor-pointer font-medium"
                >
                  <FileText className="w-3.5 h-3.5" />
                  What&apos;s included in your comprehensive PDF report?
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 flex justify-between items-center text-sm text-slate-500">
              <button
                onClick={() => setStep(3)}
                className="hover:text-slate-800 transition-colors cursor-pointer"
              >
                ← Back to Results
              </button>
              <span>Step 4: Download Report</span>
            </div>
          </div>
        )}

        {/* ── STEP 2: PHOTO UPLOAD ──────────────────────────────────────────── */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500 flex-1 flex flex-col justify-between py-2">
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#161e2a] mb-2">
                  Upload Your 2 Posture Photos
                </h2>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  We need exactly <span className="text-[#161e2a] font-semibold">2 photos</span> (Front &amp; Side) to calculate your biomechanical angles.
                </p>
              </div>

              {/* On-Demand Positioning Guide Trigger Card */}
              <div className="mb-6 max-w-sm sm:max-w-md mx-auto">
                <button
                  type="button"
                  onClick={() => setShowPositioningGuide(!showPositioningGuide)}
                  className="w-full p-4 rounded-2xl border border-[#ecf0f1] bg-white hover:bg-slate-50 transition-all flex items-center justify-between text-left shadow-sm cursor-pointer group"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-[#8e44ad] shrink-0 border border-purple-100">
                      <Camera className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-extrabold text-sm sm:text-base text-[#161e2a] flex items-center gap-1.5 leading-snug">
                        <span>📸</span> Photo Staging Guide &amp; Tips
                      </div>
                      <p className="text-sm sm:text-base text-slate-700 mt-0.5">
                        Get the most accurate AI posture scan results
                      </p>
                    </div>
                  </div>
                  <div className="text-[#595959] group-hover:text-[#8e44ad] transition-colors p-1">
                    {showPositioningGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>
              </div>

              {/* On-Demand Positioning Guide Collapsible */}
              {showPositioningGuide && (
                <div className="mb-6 p-4 rounded-xl bg-white border border-slate-200 shadow-sm animate-in fade-in duration-300 text-sm space-y-4">
                  {/* CONOR HARRIS EXAMPLE PICTURES */}
                  <div>
                    <span className="font-bold text-slate-900 block mb-2 text-center text-sm">
                      📸 Conor Harris Stance Examples (Good Photo Positioning)
                    </span>
                    <div className="grid grid-cols-2 gap-3 justify-items-center">
                      <div className="flex flex-col items-center gap-1">
                        <img
                          src="/self-assessment/conor-front.jpeg"
                          alt="Conor Harris Front View Example"
                          className="h-36 sm:h-44 w-auto rounded-lg border border-slate-200 shadow-sm object-cover"
                        />
                        <span className="text-xs font-semibold text-slate-600">Front View</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <img
                          src="/self-assessment/conor-right.jpeg"
                          alt="Conor Harris Side View Example"
                          className="h-36 sm:h-44 w-auto rounded-lg border border-slate-200 shadow-sm object-cover"
                        />
                        <span className="text-xs font-semibold text-slate-600">Side View</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-slate-800 pt-2 border-t border-slate-200">
                    <div className="p-3 rounded-lg bg-emerald-50/80 border border-emerald-200">
                      <div className="font-bold text-emerald-800 mb-1 flex items-center gap-1">
                        <Check className="w-3.5 h-3.5 text-emerald-600" /> Good Photo
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-slate-700">
                        <li>Fitted clothes (or shorts/t-shirt)</li>
                        <li>Full body in frame (head to feet)</li>
                        <li>Natural, relaxed posture</li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50/80 border border-red-200">
                      <div className="font-bold text-red-800 mb-1 flex items-center gap-1">
                        <X className="w-3.5 h-3.5 text-red-600" /> Avoid
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-slate-700">
                        <li>Baggy hoodies or jackets</li>
                        <li>Cropped photos (missing feet/head)</li>
                        <li>Forced stiff posture</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* PHOTO UPLOADER BOXES - FOCAL POINT */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* 1. FRONT PHOTO DROPZONE */}
                <div className="relative flex flex-col gap-2">
                  <input
                    type="file"
                    ref={frontInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePhotoChange(e, 'front')}
                  />
                  {frontPhoto.previewUrl ? (
                    <div
                      onClick={() => frontInputRef.current?.click()}
                      className="relative rounded-2xl overflow-hidden border-2 border-emerald-500 bg-slate-900 h-64 sm:h-80 aspect-[3/4] max-w-full mx-auto flex items-center justify-center group shadow-md cursor-pointer active:scale-[0.99] transition-transform w-full p-1"
                    >
                      <img
                        src={frontPhoto.previewUrl}
                        alt="Front posture"
                        className="w-full h-full object-contain rounded-xl"
                      />
                      <div className="absolute top-3 left-3 bg-emerald-600 text-white text-xs font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Front Ready
                      </div>
                      
                      {/* Touch-Friendly Action Buttons (Always Visible on Mobile) */}
                      <div className="absolute bottom-3 right-3 bg-slate-900/85 hover:bg-slate-900 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg backdrop-blur-sm transition-all border border-slate-700/50">
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Change Photo</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto('front');
                        }}
                        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-slate-900/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => frontInputRef.current?.click()}
                      className="w-full h-64 sm:h-80 aspect-[3/4] rounded-2xl border-2 border-dashed border-slate-300 hover:border-indigo-500 bg-white hover:bg-slate-50 transition-all flex flex-col items-center justify-center p-6 text-center group cursor-pointer shadow-sm"
                    >
                      <div className="w-14 h-14 rounded-full bg-indigo-50 text-indigo-600 group-hover:scale-110 group-hover:bg-indigo-100 transition-all flex items-center justify-center mb-3">
                        <Camera className="w-7 h-7" />
                      </div>
                      <span className="font-bold text-slate-900 text-base mb-1">
                        1. Front Photo
                      </span>
                      <span className="text-sm text-slate-500">
                        Tap to upload front view
                      </span>
                    </button>
                  )}

                  {/* Embedded Conor's Example Picture Collapsible */}
                  <div className="w-full font-sans mt-1">
                    <button
                      type="button"
                      onClick={() => setShowFrontConorExample((prev) => !prev)}
                      className="w-full py-2.5 px-3.5 flex items-center justify-between border border-[#ecf0f1] bg-white hover:bg-slate-50 rounded-2xl transition-colors text-left shadow-xs cursor-pointer"
                    >
                      <span className="text-sm font-extrabold text-[#161e2a] flex items-center gap-1.5">
                        <span>📸</span> Conor's example picture
                      </span>
                      {showFrontConorExample ? (
                        <ChevronUp className="h-4 w-4 text-[#595959]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#595959]" />
                      )}
                    </button>

                    {showFrontConorExample && (
                      <div className="mt-2 p-3 bg-white border border-[#ecf0f1] rounded-2xl flex flex-col items-center gap-2 animate-in fade-in duration-200 shadow-sm">
                        <div className="w-full rounded-xl overflow-hidden border border-slate-200 shadow-xs flex justify-center bg-slate-50 p-1">
                          <img
                            src="/self-assessment/conor-front.jpeg"
                            alt="Conor's Example Front Stance"
                            className="w-full h-auto object-contain max-h-96 rounded-lg"
                          />
                        </div>
                        <span className="text-sm font-bold text-[#161e2a] text-center pt-1">
                          Example Front Stance
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-base text-slate-700 leading-relaxed px-1">
                    Stand tall facing camera, feet shoulder-width apart, arms relaxed by your side.
                  </p>
                </div>

                {/* 2. SIDE PHOTO DROPZONE */}
                <div className="relative flex flex-col gap-2">
                  <input
                    type="file"
                    ref={sideInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePhotoChange(e, 'side')}
                  />
                  {sidePhoto.previewUrl ? (
                    <div
                      onClick={() => sideInputRef.current?.click()}
                      className="relative rounded-2xl overflow-hidden border-2 border-emerald-500 bg-slate-900 h-64 sm:h-80 aspect-[3/4] max-w-full mx-auto flex items-center justify-center group shadow-md cursor-pointer active:scale-[0.99] transition-transform w-full p-1"
                    >
                      <img
                        src={sidePhoto.previewUrl}
                        alt="Side posture"
                        className="w-full h-full object-contain rounded-xl"
                      />
                      <div className="absolute top-3 left-3 bg-emerald-600 text-white text-xs font-extrabold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Side Ready
                      </div>

                      {/* Touch-Friendly Action Buttons (Always Visible on Mobile) */}
                      <div className="absolute bottom-3 right-3 bg-slate-900/85 hover:bg-slate-900 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg backdrop-blur-sm transition-all border border-slate-700/50">
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Change Photo</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto('side');
                        }}
                        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-slate-900/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => sideInputRef.current?.click()}
                      className="w-full h-64 sm:h-80 aspect-[3/4] rounded-2xl border-2 border-dashed border-slate-300 hover:border-indigo-500 bg-white hover:bg-slate-50 transition-all flex flex-col items-center justify-center p-6 text-center group cursor-pointer shadow-sm"
                    >
                      <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 group-hover:scale-110 group-hover:bg-blue-100 transition-all flex items-center justify-center mb-3">
                        <Camera className="w-7 h-7" />
                      </div>
                      <span className="font-bold text-slate-900 text-base mb-1">
                        2. Side Photo
                      </span>
                      <span className="text-sm text-slate-500">
                        Tap to upload side profile view
                      </span>
                    </button>
                  )}

                  {/* Embedded Conor's Example Picture Collapsible */}
                  <div className="w-full font-sans mt-1">
                    <button
                      type="button"
                      onClick={() => setShowSideConorExample((prev) => !prev)}
                      className="w-full py-2.5 px-3.5 flex items-center justify-between border border-[#ecf0f1] bg-white hover:bg-slate-50 rounded-2xl transition-colors text-left shadow-xs cursor-pointer"
                    >
                      <span className="text-sm font-extrabold text-[#161e2a] flex items-center gap-1.5">
                        <span>📸</span> Conor's example picture
                      </span>
                      {showSideConorExample ? (
                        <ChevronUp className="h-4 w-4 text-[#595959]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#595959]" />
                      )}
                    </button>

                    {showSideConorExample && (
                      <div className="mt-2 p-3 bg-white border border-[#ecf0f1] rounded-2xl flex flex-col items-center gap-2 animate-in fade-in duration-200 shadow-sm">
                        <div className="w-full rounded-xl overflow-hidden border border-slate-200 shadow-xs flex justify-center bg-slate-50 p-1">
                          <img
                            src="/self-assessment/conor-right.jpeg"
                            alt="Conor's Example Right Side Stance"
                            className="w-full h-auto object-contain max-h-96 rounded-lg"
                          />
                        </div>
                        <span className="text-sm font-bold text-[#161e2a] text-center pt-1">
                          Example Right Side Stance
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-base text-slate-700 leading-relaxed px-1">
                    Turn 90° to your right. Keep relaxed posture looking straight ahead.
                  </p>
                </div>
              </div>
            </div>

            {/* ACTION & BACK BUTTON */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <button
                disabled={!frontPhoto.previewUrl || !sidePhoto.previewUrl}
                onClick={() => startAnalysisSequence()}
                className={`w-full py-4 px-6 rounded-2xl font-extrabold text-lg flex items-center justify-center gap-2 transition-all duration-200 ${
                  frontPhoto.previewUrl && sidePhoto.previewUrl
                    ? 'bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-500 hover:to-indigo-500 text-white shadow-xl shadow-emerald-600/20 cursor-pointer hover:scale-[1.01]'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-300/60'
                }`}
              >
                <span>Continue</span>
                <ArrowRight className="w-5 h-5" />
              </button>

              <div className="flex justify-between items-center text-sm text-slate-500">
                <button
                  onClick={() => setStep(1)}
                  className="hover:text-slate-800 transition-colors cursor-pointer"
                >
                  ← Back
                </button>
                <span>{!frontPhoto.previewUrl || !sidePhoto.previewUrl ? 'Please upload both photos to continue' : 'Both photos uploaded cleanly'}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: RESULTS ───────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500 flex-1 flex flex-col justify-between py-2">
            {isScanning ? (
              <div className="my-auto py-12 text-center space-y-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-purple-50 border border-purple-200 flex items-center justify-center text-[#8e44ad] animate-pulse">
                  <RefreshCw className="w-10 h-10 animate-spin text-[#8e44ad]" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-[#161e2a]">Analyzing Biomechanics</h3>
                  <p className="text-sm text-[#595959] font-medium">{scanStatusText}</p>
                </div>
                <div className="max-w-xs mx-auto bg-slate-200 h-2 rounded-full overflow-hidden border border-[#ecf0f1]">
                  <div
                    className="bg-gradient-to-r from-[oklch(52%_0.18_305)] to-emerald-500 h-full transition-all duration-300"
                    style={{ width: `${scanProgress}%` }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-sm text-slate-500 hover:text-slate-800 cursor-pointer"
                >
                  ← Back
                </button>
              </div>
            ) : analysisError || !liveSummary ? (
              <div className="my-auto py-12 text-center space-y-4" data-testid="simple-sa-analysis-error">
                <AlertCircle className="w-12 h-12 mx-auto text-rose-600" />
                <h3 className="text-xl font-bold text-[#161e2a]">We could not finish the live analysis</h3>
                <p className="text-sm text-[#595959] max-w-md mx-auto">
                  Landmark detection or classification did not complete. Please go back and try again with both photos.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAnalysisError(false);
                    setStep(2);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#ecf0f1] bg-white text-sm font-semibold text-[#161e2a] cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" />
                  Back to photos
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-sm text-slate-500 hover:text-slate-800 cursor-pointer"
                >
                  ← Back
                </button>
                {/* EMBEDDED CONOR HARRIS VIDEO PLAYER — above analysis results */}
                <div className="rounded-2xl overflow-hidden shadow-lg border border-[#ecf0f1]">
                  <ConorHarrisVideoPlayer
                    title="What Your Posture Assessment Means & How to Fix It"
                  />
                </div>

                {/* PRIMARY CTA: more detail so the next step can fit the results */}
                <div className="space-y-3 pt-2">
                  <p className="text-center sm:text-left font-bold text-lg sm:text-xl text-[#161e2a] font-['Poppins',sans-serif]">
                    {SIMPLE_SA_SURVEY_LEAD_IN}
                  </p>
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-[#8e44ad] hover:from-emerald-500 hover:to-purple-800 text-white font-black text-xl shadow-xl shadow-emerald-900/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-3 border border-emerald-500/20 cursor-pointer"
                  >
                    <ClipboardList className="w-6 h-6 text-white" />
                    <span>{SIMPLE_SA_SURVEY_HEADLINE}</span>
                  </button>

                  <div className="text-center">
                    <button
                      onClick={() => setStep(4)}
                      className="text-sm text-[#595959] hover:text-[#161e2a] underline underline-offset-4 inline-flex items-center gap-1 cursor-pointer font-medium"
                    >
                      <span>Or download your comprehensive PDF report</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* HIGH IMPACT CLINICAL CLASSIFICATION SUMMARY */}
                <div className="bg-white border border-[#ecf0f1] rounded-2xl p-5 sm:p-6 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />

                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-amber-50 text-amber-800 border-amber-200 font-semibold text-xs sm:text-sm">
                      Primary Biomechanical Classification
                    </Badge>
                  </div>

                  <h2
                    className="text-2xl font-black text-[#161e2a] mb-2 tracking-tight"
                    data-testid="simple-sa-pattern-title"
                  >
                    {liveSummary?.patternTitle}
                  </h2>
                  <p
                    className="text-base sm:text-lg text-slate-700 font-medium leading-relaxed mb-4"
                    data-testid="simple-sa-compensations"
                  >
                    {liveSummary?.compensationsLine}
                  </p>

                  {/* POSTURE PICS WITH OVERLAID LANDMARKS & ANGLES */}
                  <div className="mt-4 pt-4 border-t border-[#ecf0f1]">
                    <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#595959] mb-3 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#8e44ad]" />
                      Landmark &amp; Angle Diagnostics
                    </h4>

                    <div className="grid grid-cols-2 gap-3">
                      {/* FRONT VIEW DIAGNOSTIC CARD */}
                      <div className="bg-[#f9f9f9] rounded-xl p-3 border border-[#ecf0f1] relative flex flex-col items-center">
                        <div className="w-full rounded-lg overflow-hidden bg-slate-900 mb-2 border border-slate-300 flex items-center justify-center p-1 min-h-[220px]">
                          {frontPhoto.previewUrl ? (
                            <AnnotatedImage view="Anterior" analysis={simplePostureAnalysis} onLandmarksDetected={handleLandmarksDetected} />
                          ) : (
                            <div className="w-full h-48 flex items-center justify-center bg-slate-900 text-slate-400 text-xs">Front View</div>
                          )}
                        </div>
                        <div className="text-center w-full">
                          <span className="text-xs sm:text-sm font-bold text-[#161e2a] block">Anterior Alignment</span>
                          <span className="text-xs sm:text-sm font-semibold text-amber-700" data-testid="simple-sa-shoulder-tilt">
                            {liveSummary?.shoulderTiltLabel}
                          </span>
                        </div>
                      </div>

                      {/* SIDE VIEW DIAGNOSTIC CARD */}
                      <div className="bg-[#f9f9f9] rounded-xl p-3 border border-[#ecf0f1] relative flex flex-col items-center">
                        <div className="w-full rounded-lg overflow-hidden bg-slate-900 mb-2 border border-slate-300 flex items-center justify-center p-1 min-h-[220px]">
                          {sidePhoto.previewUrl ? (
                            <AnnotatedImage view="Right" analysis={simplePostureAnalysis} onLandmarksDetected={handleLandmarksDetected} />
                          ) : (
                            <div className="w-full h-48 flex items-center justify-center bg-slate-900 text-slate-400 text-xs">Side View</div>
                          )}
                        </div>
                        <div className="text-center w-full">
                          <span className="text-xs sm:text-sm font-bold text-[#161e2a] block">Sagittal Profile</span>
                          <span className="text-xs sm:text-sm font-semibold text-rose-700" data-testid="simple-sa-tibial">
                            {liveSummary?.tibialInclinationLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* DIGESTIBLE KEY ANGLES SUMMARY */}
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#ecf0f1] text-center" data-testid="simple-sa-metric-tiles">
                    {(liveSummary?.tiles ?? []).map((tile) => (
                      <div key={tile.label} className="p-2 rounded-lg bg-[#f9f9f9] border border-[#ecf0f1]">
                        <span className="text-xs text-[#595959] block">{tile.label}</span>
                        <span className="text-base font-bold text-[#161e2a]">{tile.value}</span>
                        <span className="text-[11px] sm:text-xs text-[#595959] block">{tile.qualifier}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* ── ON-DEMAND MODALS / DIALOGS ("BEAMED-IN" SYSTEM) ──────────────── */}

        {/* 1. HOW IT WORKS MODAL */}
        <Dialog open={showHowItWorksModal} onOpenChange={setShowHowItWorksModal}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2 text-slate-900">
                <Sparkles className="w-5 h-5 text-indigo-600" /> How Biomechanical AI Works
              </DialogTitle>
              <DialogDescription className="text-slate-600 text-sm">
                Our computer vision model analyzes 33 skeletal landmarks to evaluate pelvic tilt, shoulder asymmetry, and forward head shift.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700 py-2">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                <span className="font-bold text-slate-900">1. Landmark Recognition</span>
                <p className="text-slate-600">The model locates key joints: ASIS, PSIS, acromion, tragus, and lateral malleolus.</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                <span className="font-bold text-slate-900">2. Angle Calculation</span>
                <p className="text-slate-600">Measures sagittal pelvic tilt angle against clinical normative standards.</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                <span className="font-bold text-slate-900">3. Pattern Classification</span>
                <p className="text-slate-600">Classifies posture into specific biomechanical compensation patterns (e.g., APT, Swayback, lateralization).</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* 2. PRIVACY MODAL */}
        <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2 text-slate-900">
                <Lock className="w-5 h-5 text-emerald-600" /> Data Privacy &amp; Encryption
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700 py-2">
              <p className="font-medium text-slate-800">Your privacy is strictly protected:</p>
              <ul className="list-disc list-inside space-y-1.5 text-slate-600">
                <li>Photos are processed securely and never sold to third parties.</li>
                <li>You can delete your photos and data at any time.</li>
              </ul>
            </div>
          </DialogContent>
        </Dialog>

        {/* 3. BOOKING CONSULTATION — viewport dialog, no CSS transform.
            Centered DialogContent uses translate(-50%) + zoom-in, which breaks
            hit-testing inside the cross-origin GHL iframe (Next is untappable
            on mobile; desktop clicks land offset). Fullscreen path skips that. */}
        <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
          {showBookingModal ? (
          <DialogContent
            translate="no"
            className="notranslate bg-background border-border text-foreground w-screen max-w-none h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col p-3 sm:p-6 max-md:p-0 max-md:pointer-events-none shadow-2xl rounded-none"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>{SIMPLE_SA_SURVEY_HEADLINE}</DialogTitle>
              <DialogDescription>Answer a few questions so we can recommend the most useful next step for your results.</DialogDescription>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <SurveyBookingWidget
                name={leadName}
                email={leadEmail}
                postureClassification={liveAnalysis?.classification?.pattern}
                postureAnalysisUrl={
                  typeof window === 'undefined'
                    ? ''
                    : `${window.location.origin}${window.location.pathname}`
                }
                surveyUrl={DEFAULT_SURVEY_BASE_URL}
                headline={SIMPLE_SA_SURVEY_HEADLINE}
                bookingWidgetUrl=""
                fillViewport
                onEnsureProspectForBooking={ensureProspectForBooking}
                onSurveyContact={rememberLeadContact}
                onSurveySubmitted={(contact) => {
                  rememberLeadContact(contact);
                  trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.applicationSubmitted);
                  logger.info('[SelfAssessmentSimplePage] Survey booking submitted successfully');
                }}
                onDownloadReport={() => {
                  setShowBookingModal(false);
                  setStep(4);
                }}
                onBookingScheduled={() => {
                  trackHyrosFunnelEvent(HYROS_FUNNEL_EVENTS.callBooked);
                  void persistProspectGhlCalendarEvent(bookingProspectIdRef.current, 'booked');
                  logger.info('[SelfAssessmentSimplePage] Meta Schedule fired for GHL booking');
                }}
              />

              <button
                type="button"
                className="pointer-events-auto fixed bottom-4 left-4 z-[70] md:hidden rounded-xl bg-background border border-border px-4 py-2.5 text-sm font-bold text-foreground shadow-lg"
                onClick={() => {
                  setShowBookingModal(false);
                  if (!liveSummary) setStep(2);
                }}
              >
                ← Back
              </button>
              <div
                className="max-md:hidden flex shrink-0 flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-border text-xs"
                data-testid="survey-booking-footer"
              >
                <button
                  onClick={() => {
                    setShowBookingModal(false);
                    if (!liveSummary) setStep(2);
                  }}
                  className="w-full sm:w-auto py-2.5 px-4 rounded-xl bg-muted hover:bg-accent text-foreground font-bold flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  <span>Back to Posture Report &amp; Download PDF</span>
                </button>

                <span className="text-muted-foreground text-xs sm:text-sm text-center sm:text-right">
                  Your answers help us recommend the most useful next step.
                </span>
              </div>
            </div>
          </DialogContent>
          ) : null}
        </Dialog>

        {/* 4. PDF REPORT PREVIEW MODAL */}
        <Dialog open={showPdfPreviewModal} onOpenChange={setShowPdfPreviewModal}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2 text-slate-900">
                <FileText className="w-5 h-5 text-indigo-600" /> Comprehensive PDF Report Breakdown
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700 py-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span><strong>Detailed Joint Angles:</strong> Pelvic tilt, thoracic curve, cervical forward shift.</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span><strong>Corrective Exercise Protocols:</strong> Daily 10-minute routines tailored to your pattern.</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SelfAssessmentSimplePage;
